import {graphql, useFragment} from 'react-relay'

import type {
  useRecentAgentActivity_issue$data,
  useRecentAgentActivity_issue$key,
} from './__generated__/useRecentAgentActivity_issue.graphql'

export type AgentActionType = 'label_added' | 'type_set' | 'field_set'

export type LabelInfo = {
  name: string
  color: string | null
}

export type AgentAction = {
  type: AgentActionType
  fieldName: string
  value: string | null
  createdAt: string
  color: string | null
  labels?: LabelInfo[]
}

export type AgentActivityGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  actions: AgentAction[]
}

export type ProcessedAgentActivity = {
  hasRecentActivity: boolean
  groups: AgentActivityGroup[]
  totalActionCount: number
}

export type RecentAgentActivityResult = ProcessedAgentActivity & {
  issueId: string
  viewerCanUpdateMetadata: boolean
  repoNameWithOwner: string
}

type LabelAction = AgentAction & {labels: LabelInfo[]}

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function computeRecentActivitySince(): string {
  const hourMs = 60 * 60 * 1000
  const rounded = Math.floor((Date.now() - SEVEN_DAYS_MS) / hourMs) * hourMs
  return new Date(rounded).toISOString()
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const recentAgentActivityFragment = graphql`
  fragment useRecentAgentActivity_issue on Issue @argumentDefinitions(since: {type: "DateTime"}) {
    id
    viewerCanUpdateMetadata
    repository {
      nameWithOwner
    }
    eventRationales {
      rationale
      issueEvent {
        ... on LabeledEvent {
          id
        }
        ... on UnlabeledEvent {
          id
        }
        ... on IssueTypeAddedEvent {
          id
        }
        ... on IssueTypeChangedEvent {
          id
        }
        ... on IssueTypeRemovedEvent {
          id
        }
        ... on IssueFieldChangedEvent {
          id
        }
        ... on IssueFieldAddedEvent {
          id
        }
        ... on IssueFieldRemovedEvent {
          id
        }
      }
    }
    recentTriageEvents: timelineItems(
      last: 25
      since: $since
      visibleEventsOnly: true
      itemTypes: [
        LABELED_EVENT
        UNLABELED_EVENT
        ISSUE_TYPE_ADDED_EVENT
        ISSUE_TYPE_CHANGED_EVENT
        ISSUE_TYPE_REMOVED_EVENT
        ISSUE_FIELD_CHANGED_EVENT
        ISSUE_FIELD_ADDED_EVENT
        ISSUE_FIELD_REMOVED_EVENT
      ]
    ) {
      nodes {
        __typename
        ... on LabeledEvent {
          id
          createdAt
          label {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on UnlabeledEvent {
          id
          createdAt
          label {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeAddedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeChangedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueTypeRemovedEvent {
          id
          createdAt
          issueType {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldChangedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          newValue
          newColor
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldAddedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          value
          color
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
        ... on IssueFieldRemovedEvent {
          id
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              displayName
            }
          }
        }
      }
    }
  }
`

type TimelineNode = NonNullable<NonNullable<useRecentAgentActivity_issue$data['recentTriageEvents']>['nodes']>[number]

type EventRationale = NonNullable<useRecentAgentActivity_issue$data['eventRationales']>[number]

type ActorInfo = {name: string; slug: string; login: string; avatarUrl: string}

function getActorInfo(node: NonNullable<TimelineNode>): ActorInfo | null {
  if (!('actor' in node) || !node.actor) return null
  const displayName = node.actor.__typename === 'Bot' ? node.actor.displayName : undefined
  return {
    name: displayName ?? node.actor.login,
    slug: node.actor.login,
    login: node.actor.login,
    avatarUrl: node.actor.avatarUrl,
  }
}

function buildRationaleEventIds(rationales: readonly EventRationale[] | null | undefined): Set<string> {
  const ids = new Set<string>()
  if (!rationales) return ids
  for (const r of rationales) {
    if (r.rationale && r.issueEvent?.id) {
      ids.add(r.issueEvent.id)
    }
  }
  return ids
}

function getFieldKey(node: NonNullable<TimelineNode>): string | null {
  switch (node.__typename) {
    case 'LabeledEvent':
    case 'UnlabeledEvent':
      return node.label?.name ? `label:${node.label.name}` : null
    case 'IssueTypeAddedEvent':
    case 'IssueTypeChangedEvent':
    case 'IssueTypeRemovedEvent':
      return 'type'
    case 'IssueFieldChangedEvent':
    case 'IssueFieldAddedEvent':
    case 'IssueFieldRemovedEvent':
      return node.issueField?.name ? `field:${node.issueField.name}` : null
    default:
      return null
  }
}

function toAgentAction(node: NonNullable<TimelineNode>): AgentAction | null {
  switch (node.__typename) {
    case 'LabeledEvent':
      return {
        type: 'label_added',
        fieldName: 'Label',
        value: node.label?.name ?? null,
        createdAt: node.createdAt,
        color: node.label?.color ?? null,
      }
    case 'IssueTypeAddedEvent':
    case 'IssueTypeChangedEvent':
      return {
        type: 'type_set',
        fieldName: 'Type',
        value: node.issueType?.name ?? null,
        createdAt: node.createdAt,
        color: node.issueType?.color?.toLowerCase() ?? null,
      }
    case 'IssueFieldChangedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.newValue ?? null,
        createdAt: node.createdAt,
        color: node.newColor ?? null,
      }
    case 'IssueFieldAddedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.value ?? null,
        createdAt: node.createdAt,
        color: node.color ?? null,
      }
    default:
      return null
  }
}

export function processAgentActivity(
  nodes: ReadonlyArray<TimelineNode | null>,
  rationaleEventIds?: Set<string>,
): ProcessedAgentActivity {
  const seenFields = new Set<string>()
  const groupsBySlug = new Map<string, AgentActivityGroup>()
  let totalActionCount = 0

  const labelActionsBySlug = new Map<string, LabelAction>()

  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]
    if (!node || !('createdAt' in node)) continue

    // Only include events that have an attached rationale
    if (!('id' in node) || !node.id || !rationaleEventIds?.has(node.id)) continue

    const fieldKey = getFieldKey(node)
    if (!fieldKey || seenFields.has(fieldKey)) continue

    const actor = getActorInfo(node)
    if (!actor) {
      seenFields.add(fieldKey)
      continue
    }

    const action = toAgentAction(node)
    if (!action) continue

    seenFields.add(fieldKey)

    let group = groupsBySlug.get(actor.slug)
    if (!group) {
      group = {
        agentName: actor.name,
        agentSlug: actor.slug,
        actorLogin: actor.login,
        actorAvatarUrl: actor.avatarUrl,
        actions: [],
      }
      groupsBySlug.set(actor.slug, group)
    }

    if (action.type === 'label_added') {
      const labelInfo: LabelInfo = {name: action.value ?? '', color: action.color}
      const existing = labelActionsBySlug.get(actor.slug)
      if (existing) {
        existing.labels.push(labelInfo)
        if (action.createdAt > existing.createdAt) {
          existing.createdAt = action.createdAt
        }
      } else {
        const labelAction: LabelAction = {
          ...action,
          fieldName: 'Labels',
          labels: [labelInfo],
        }
        labelActionsBySlug.set(actor.slug, labelAction)
        group.actions.unshift(labelAction)
      }
      totalActionCount++
    } else {
      group.actions.unshift(action)
      totalActionCount++
    }
  }

  const groups = Array.from(groupsBySlug.values())
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))

  return {
    hasRecentActivity: totalActionCount > 0,
    groups,
    totalActionCount,
  }
}

export function useRecentAgentActivity(issueRef: useRecentAgentActivity_issue$key): RecentAgentActivityResult {
  const data = useFragment(recentAgentActivityFragment, issueRef)
  const nodes = data.recentTriageEvents?.nodes ?? []
  const rationaleEventIds = buildRationaleEventIds(data.eventRationales)
  const result = processAgentActivity(nodes, rationaleEventIds)
  return {
    ...result,
    issueId: data.id,
    viewerCanUpdateMetadata: data.viewerCanUpdateMetadata ?? false,
    repoNameWithOwner: data.repository.nameWithOwner,
  }
}
